/**
 * Canonical traditions catalogue for the Traditions index + detail pages.
 *
 * Each entry is intentionally tight — a primer, not an essay. Detail pages
 * pair this with the Oracle's deeper synthesis when needed.
 *
 * Slug rules: lowercase, hyphen-separated, ASCII only. Stable forever — these
 * become URLs (/tradition/<slug>).
 */
const T = (o) => ({ ...o, slug: o.slug });

export const TRADITIONS = [
  T({
    slug: "christianity", name: "Christianity",
    era: "1st century CE — present",
    region: "Levant → Mediterranean → global",
    summary: "A monotheistic faith centered on Jesus of Nazareth as the Logos made flesh — the eternal Word entering history to reconcile humanity with God through love.",
    beliefs: [
      "One God in three persons (Trinity): Father, Son, Holy Spirit.",
      "Christ as the Logos — the rational, generative principle through which all things were made.",
      "Imago Dei: every human bears the image of God.",
      "Salvation through grace; love of God and neighbour as the highest law.",
    ],
    practices: ["Prayer (lectio divina, contemplative silence)", "Eucharist / communion", "Baptism", "Scripture reading", "Sabbath observance"],
    figures: ["Jesus of Nazareth", "Paul of Tarsus", "Augustine", "Meister Eckhart", "Teresa of Ávila"],
    correlations: ["Logos ↔ Tao ↔ Brahman ↔ Aum", "Imago Dei ↔ Atman ↔ Buddha-nature"],
  }),
  T({
    slug: "judaism", name: "Judaism",
    era: "~1500 BCE — present",
    region: "Levant → global diaspora",
    summary: "The covenant tradition — a people called into relationship with the One God through Torah, ethical command, and the long arc of repair (tikkun olam). The parent stream from which Christianity and Islam emerge, and from which Kabbalah ascends inward.",
    beliefs: [
      "Shema: 'Hear, O Israel: the Lord our God, the Lord is One.'",
      "Covenant: a binding relationship between God and a people, through history.",
      "Torah as both written and oral — a living, argued tradition.",
      "Tikkun olam: the human task is to repair the world.",
      "B'tzelem Elohim: every person is made in the image of God.",
    ],
    practices: ["Shabbat (weekly rest)", "Daily prayer (Shema, Amidah)", "Study of Torah + Talmud", "The festival cycle (Pesach, Yom Kippur, Sukkot)", "Mitzvot — ethical and ritual commandments", "Mikveh, kashrut, life-cycle rites"],
    figures: ["Abraham, Moses, the Prophets", "Hillel + Shammai", "Maimonides (Rambam)", "Rashi", "Martin Buber", "Abraham Joshua Heschel"],
    correlations: ["Tikkun olam ↔ bodhisattva vow ↔ karma yoga", "Shabbat ↔ contemplative pause ↔ wu wei", "B'tzelem Elohim ↔ Imago Dei ↔ Atman ↔ Buddha-nature"],
  }),
  T({
    slug: "book-of-enoch", name: "The Book of Enoch",
    era: "~3rd century BCE — 1st century CE (Second Temple period)",
    region: "Judea → Ethiopia (preserved in Ge'ez) → global esoteric currents",
    summary: "A constellation of apocalyptic Jewish texts attributed to the antediluvian patriarch Enoch — the one 'taken' by God without dying. Records the descent of the Watchers, the origin of forbidden knowledge, the Nephilim, and Enoch's tours through the heavens. Foundational to Christian angelology, the Dead Sea Scrolls community, and Ethiopian Orthodox canon.",
    beliefs: [
      "The Watchers: a class of angels who descended to earth, took human wives, and taught forbidden arts (metallurgy, astrology, cosmetics, sorcery).",
      "The Nephilim: the giant offspring whose violence triggers the Flood judgment.",
      "Enoch as the righteous scribe — taken alive into the heavens and shown the cosmic order.",
      "A coming judgment of the wicked and reward of the righteous (proto-apocalyptic eschatology).",
      "Hidden wisdom: cosmology, astronomy, and the calendar revealed by angels.",
    ],
    practices: ["Apocalyptic reading + interpretation", "Calendrical observance (solar 364-day calendar)", "Mystical contemplation of the heavenly throne (proto-merkabah)", "Preserved liturgical reading in the Ethiopian Orthodox Tewahedo Church"],
    figures: ["Enoch (Hanok ben Yared)", "the Watchers (Shemyaza, Azazel, etc.)", "the Dead Sea / Qumran community", "Ethiopian Orthodox scholars who preserved the text"],
    correlations: ["Watchers' descent ↔ Greek Titans / Promethean myth ↔ falling devas", "Enoch's ascent ↔ shamanic sky-journey ↔ merkabah ↔ mi'raj ↔ Dante's Paradiso", "Forbidden knowledge ↔ Eden's tree ↔ Promethean fire"],
  }),
  T({
    slug: "islam", name: "Islam",
    era: "7th century CE — present",
    region: "Arabian Peninsula → global",
    summary: "The path of submission to the One God (Allah) revealed through the Qur'an to the Prophet Muhammad — completing, in its own account, the earlier prophetic stream of Judaism and Christianity. A faith of radical unity (Tawhid), mercy, and lived discipline.",
    beliefs: [
      "Tawhid: God is absolutely One — no partner, no division, no image.",
      "Prophethood: a single message carried through Adam, Abraham, Moses, Jesus, and sealed in Muhammad.",
      "The Qur'an as the literal speech of God, transmitted in Arabic.",
      "Akhirah: a Day of Judgement and a moral arc to existence.",
      "Mercy (rahma) as God's primary attribute — every chapter opens 'In the name of God, the Merciful, the Compassionate.'",
    ],
    practices: ["The Five Pillars: Shahada, Salah (5 daily prayers), Zakat (almsgiving), Sawm (Ramadan fast), Hajj (pilgrimage)", "Recitation of the Qur'an", "Halal dietary discipline", "Communal Friday prayer (jumu'ah)"],
    figures: ["The Prophet Muhammad", "Ali ibn Abi Talib", "Aisha bint Abu Bakr", "Al-Ghazali", "Ibn Sina (Avicenna)", "Fatima al-Fihri"],
    correlations: ["Tawhid ↔ Ein Sof ↔ Brahman ↔ apophatic Godhead", "Submission (islam) ↔ surrender ↔ wu wei", "Rahma ↔ chesed ↔ karuna ↔ agape"],
  }),
  T({
    slug: "hinduism", name: "Hinduism",
    era: "~1500 BCE — present",
    region: "Indian subcontinent → global diaspora",
    summary: "The vast living tradition of Sanatana Dharma — the 'eternal way' — that holds Vedanta, Tantra, Yoga, Bhakti, and countless local lineages under one roof. Not a single creed but a family of paths agreeing on a few profound things: that reality is One (Brahman), that the Self (Atman) is its mirror, and that there are many true ways home.",
    beliefs: [
      "Brahman: an unconditioned ultimate reality that takes form (saguna) and is also beyond all form (nirguna).",
      "Atman is Brahman — the deepest self of any being is, in its essence, the One.",
      "Samsara (the cycle of birth-death-rebirth), karma (cause and effect across lifetimes), and moksha (release into the One).",
      "The four aims of a human life — dharma (duty/right-living), artha (livelihood), kama (love/desire), moksha (liberation).",
      "Many paths to the same end: jnana (knowledge), bhakti (devotion), karma (right action), raja (meditation).",
      "The divine is one face seen in many: Vishnu, Shiva, Devi, Ganesha, and a thousand more.",
    ],
    practices: ["Puja (devotional offering)", "Mantra and japa (Om / sacred name repetition)", "Yoga (the eightfold path of Patanjali, plus bhakti, karma, jnana yogas)", "Festival cycle (Diwali, Holi, Navaratri, Maha Shivaratri)", "Pilgrimage (Varanasi, Rishikesh, Kumbh Mela)", "Study of the Bhagavad Gita, Upanishads, Ramayana, Mahabharata"],
    figures: ["Vyasa", "Patanjali", "Adi Shankaracharya", "Ramanuja", "Sri Ramakrishna", "Swami Vivekananda", "Sri Aurobindo", "Ramana Maharshi"],
    correlations: ["Brahman ↔ Tao ↔ Ein Sof ↔ Hermetic 'The All'", "Atman ↔ Imago Dei ↔ Buddha-nature ↔ the divine spark", "Dharma ↔ Logos as cosmic order ↔ Tao as the Way", "Karma ↔ the principle of cause and effect across traditions", "Trimurti (creation–preservation–dissolution) ↔ Trinitarian patterns ↔ alchemical phases", "Bhakti ↔ Sufism's ishq ↔ Christian agape"],
  }),
  T({
    slug: "vedanta", name: "Vedanta",
    era: "1500 BCE — present (formalised ~800 BCE)",
    region: "Indian subcontinent → global",
    summary: "The 'end of the Vedas' — Hindu philosophical schools centered on the identity of Atman (the self) with Brahman (the absolute), and the dissolution of the illusion (Maya) that separates them.",
    beliefs: [
      "Brahman: the one absolute reality, beyond name and form.",
      "Atman: the innermost self, identical to Brahman ('Tat tvam asi' — Thou art That).",
      "Maya: the illusory appearance of separation.",
      "Moksha: liberation through realization, not works.",
    ],
    practices: ["Self-inquiry (atma-vichara)", "Meditation (dhyana)", "Mantra (Aum)", "Karma yoga (selfless action)", "Bhakti (devotion)"],
    figures: ["Adi Shankara", "Ramana Maharshi", "Swami Vivekananda", "Nisargadatta Maharaj"],
    correlations: ["Brahman ↔ Tao ↔ Logos ↔ Ein Sof", "Atman ↔ Imago Dei ↔ Buddha-nature"],
  }),
  T({
    slug: "buddhism", name: "Buddhism",
    era: "~500 BCE — present",
    region: "India → East & Southeast Asia → global",
    summary: "The path of the Buddha — Siddhartha Gautama's diagnosis of suffering (dukkha), its cause (craving), its cessation (nibbana), and the practical eightfold path to that liberation.",
    beliefs: [
      "The Four Noble Truths: suffering, its cause, its end, and the path.",
      "Anatta: no fixed, separate self.",
      "Anicca: all things are impermanent.",
      "Karuna (compassion) and metta (loving-kindness) as the felt face of awakening.",
    ],
    practices: ["Meditation (samatha, vipassana, zazen)", "The Eightfold Path", "Mindfulness", "Loving-kindness practice", "Refuge in the Three Jewels"],
    figures: ["Gautama Buddha", "Nagarjuna", "Bodhidharma", "Thich Nhat Hanh", "the Dalai Lama"],
    correlations: ["Sunyata (emptiness) ↔ Ein Sof ↔ apophatic God", "Bodhicitta ↔ agape ↔ karuna"],
  }),
  T({
    slug: "tibetan-buddhism", name: "Tibetan Buddhism",
    era: "7th century CE — present",
    region: "Tibetan plateau, Himalayan region, diaspora",
    summary: "Vajrayana ('Diamond Vehicle') — a tantric, ritually rich form of Buddhism that fuses Indian Mahayana with the indigenous Bön tradition, emphasising direct realization within this very lifetime.",
    beliefs: [
      "The mind's true nature (rigpa) is already enlightened.",
      "Tantric union of method (compassion) and wisdom (emptiness).",
      "Multiple Buddhas, bodhisattvas, and protectors as facets of mind.",
      "Bardo: intermediate states between death and rebirth.",
    ],
    practices: ["Deity visualization (sadhana)", "Mantra (Om Mani Padme Hum)", "Dzogchen and Mahamudra meditation", "Tonglen (taking-and-sending compassion)", "Mandala practice"],
    figures: ["Padmasambhava", "Milarepa", "Tsongkhapa", "The Dalai Lamas", "Chögyam Trungpa"],
    correlations: ["Rigpa ↔ Christ-consciousness ↔ Atman-Brahman", "Bardo ↔ purgatorial / liminal states"],
  }),
  T({
    slug: "sufism", name: "Sufism",
    era: "8th century CE — present",
    region: "Islamic world → global",
    summary: "The inner, mystical heart of Islam — the path of fana ('annihilation' of the ego in God) and baqa ('subsistence in God'), expressed in poetry, music, and ecstatic devotion.",
    beliefs: [
      "Tawhid: the radical unity of God beneath every appearance.",
      "The heart (qalb) as the organ that knows God directly.",
      "Love as the ladder back to the Beloved.",
      "Stations (maqamat) and states (ahwal) on the path of return.",
    ],
    practices: ["Dhikr (remembrance / chanting the names of God)", "Sama (sacred music + whirling)", "Murshid-murid (master-disciple) lineage", "Fasting and retreat"],
    figures: ["Rumi", "Hafiz", "Ibn Arabi", "Rabi'a al-Adawiyya", "Al-Ghazali"],
    correlations: ["Fana ↔ kenosis ↔ ego-death", "Beloved ↔ Krishna ↔ Bridegroom of the Song of Songs"],
  }),
  T({
    slug: "taoism", name: "Taoism",
    era: "~500 BCE — present",
    region: "China → East Asia → global",
    summary: "The Way (Tao) that cannot be named — a Chinese tradition of effortless action (wu wei), harmony with the natural order, and the play of yin and yang as the rhythm of all becoming.",
    beliefs: [
      "The Tao precedes all things and cannot be spoken.",
      "Yin and yang: complementary opposites that generate the ten thousand things.",
      "Wu wei: action without forcing — moving with the grain of reality.",
      "Te: virtue as alignment with the Tao.",
    ],
    practices: ["Meditation (zuòwàng — sitting and forgetting)", "Qigong, Tai Chi", "Internal alchemy (neidan)", "Living simply in nature"],
    figures: ["Laozi", "Zhuangzi", "Liezi"],
    correlations: ["Tao ↔ Logos ↔ Brahman", "Wu wei ↔ surrender ↔ flow"],
  }),
  T({
    slug: "tantra", name: "Tantra",
    era: "~500 CE — present",
    region: "India, Tibet, Nepal → global",
    summary: "A family of esoteric Hindu and Buddhist paths that take the body, the senses, sexuality, and the world itself as fields for liberation — rejecting the renunciate split between sacred and profane.",
    beliefs: [
      "Shakti: the dynamic, feminine energy of consciousness itself.",
      "Shiva-Shakti union: the play of awareness and energy.",
      "The body as temple — chakras, nadis, kundalini.",
      "Every appearance is a face of the divine.",
    ],
    practices: ["Kundalini awakening", "Mantra, yantra, mudra", "Ritual sexual union (in some lineages)", "Mantra recitation"],
    figures: ["Abhinavagupta", "Sir John Woodroffe (Arthur Avalon)", "Lalla Yogeshwari"],
    correlations: ["Shakti ↔ Sophia ↔ Holy Spirit (feminine readings)", "Chakras ↔ Sephirot ↔ subtle anatomy"],
  }),
  T({
    slug: "stoicism", name: "Stoicism",
    era: "~300 BCE — present",
    region: "Greece → Rome → global revival",
    summary: "A Greco-Roman philosophy that locates the good life in virtue — accepting what we cannot control, governing what we can, and aligning the will with the rational order (logos) of nature.",
    beliefs: [
      "Virtue is the only true good.",
      "The dichotomy of control: act on what is up to you; release the rest.",
      "Logos pervades all of nature — humans share in it through reason.",
      "Death is natural, not evil.",
    ],
    practices: ["Premeditatio malorum (negative visualization)", "Daily journaling", "View from above (cosmic perspective)", "Apatheia (freedom from disturbing passions)"],
    figures: ["Zeno of Citium", "Epictetus", "Marcus Aurelius", "Seneca"],
    correlations: ["Logos (Stoic) ↔ Logos (Johannine) ↔ Tao", "Apatheia ↔ equanimity ↔ vairagya"],
  }),
  T({
    slug: "determinism", name: "Determinism",
    era: "Ancient Stoicism → Enlightenment mechanics → present neuroscience & physics",
    region: "Greece → Europe → global (parallel currents in India, Persia, China)",
    summary: "The claim that every event — including every human choice — is the fixed consequence of prior causes. Not a religion but a lens that keeps surfacing across traditions: the Stoic heimarmene, Augustinian predestination, Islamic qadar, karmic cause-and-effect, Laplace's demon, and modern debates about superdeterminism and the neural roots of the felt sense of choice.",
    beliefs: [
      "Causal closure: every event has a sufficient prior cause; nothing arises from nothing.",
      "The felt experience of 'free choice' is compatible with (or an illusion produced by) the underlying causal chain — depending on the flavor.",
      "Hard determinism: no free will, no ultimate moral desert; ethics survives as consequence-shaping.",
      "Compatibilism: 'free' means acting from one's own reasons and character — even if those are themselves caused. Freedom = self-authorship, not uncaused origination.",
      "Superdeterminism (physics): even quantum outcomes and measurement choices are fixed by hidden variables set at the Big Bang; used to rescue local realism from Bell's theorem.",
    ],
    practices: ["Radical acceptance of what is (Stoic amor fati)", "Compassion as recognition — 'they could not have done otherwise given their causes'", "Journaling to trace the causes behind a reaction", "Meditation on the arising of thoughts (they appear; you do not summon them)", "Consequentialist ethics: shape futures by shaping conditions, not by blaming"],
    figures: ["Chrysippus (Stoic)", "Augustine of Hippo", "Baruch Spinoza", "Pierre-Simon Laplace", "Arthur Schopenhauer", "Daniel Dennett (compatibilism)", "Sam Harris (hard determinism)", "Gerard 't Hooft + Sabine Hossenfelder (superdeterminism)"],
    correlations: [
      "Heimarmene (Stoic fate) ↔ predestination ↔ qadar ↔ karma ↔ Laplace's demon",
      "Amor fati ↔ 'thy will be done' ↔ islam (submission) ↔ wu wei",
      "Compatibilism ↔ karma yoga (act as instrument, not owner)",
      "No-self / anatta ↔ Advaita (no separate doer) ↔ 'you did not choose your next thought'",
      "Superdeterminism ↔ Vedic ṛta (cosmic order) ↔ Calvinist election",
    ],
  }),
  T({
    slug: "platonism", name: "Platonism",
    era: "~400 BCE — present",
    region: "Greece → Mediterranean → Christian / Islamic world",
    summary: "Plato and his heirs — a metaphysics in which the visible world is the shadow of timeless Forms, and philosophy is the soul's ascent from appearance to the Good itself.",
    beliefs: [
      "The Forms: eternal, perfect originals of which physical things are copies.",
      "The Good as the source of being and intelligibility.",
      "The soul is immortal and pre-existed.",
      "Knowledge is recollection (anamnesis).",
    ],
    practices: ["Dialectic / Socratic inquiry", "Contemplation (theoria)", "Mathematical study as soul-purification", "Cultivation of the four cardinal virtues"],
    figures: ["Plato", "Plotinus", "Iamblichus", "Proclus"],
    correlations: ["The Forms ↔ Logos ↔ Plato's Good ↔ Brahman", "Anamnesis ↔ pratyabhijna (recognition)"],
  }),
  T({
    slug: "hermeticism", name: "Hermeticism",
    era: "1st–3rd century CE → Renaissance revival → present",
    region: "Hellenistic Egypt → Europe → global",
    summary: "A Greco-Egyptian wisdom tradition attributed to the mythical Hermes Trismegistus, teaching the correspondence of macrocosm and microcosm: 'as above, so below; as within, so without.'",
    beliefs: [
      "The All is mind; the cosmos is mental.",
      "Correspondence between every level of reality.",
      "Vibration: nothing rests; everything moves.",
      "Gender and polarity as universal patterns.",
    ],
    practices: ["Theurgy (sacred ritual)", "Alchemy (inner + outer)", "Astrological correspondence", "Contemplative ascent through the seven spheres"],
    figures: ["Hermes Trismegistus (legendary)", "Marsilio Ficino", "Giordano Bruno"],
    correlations: ["'As above, so below' ↔ holography ↔ fractal cosmology", "Macrocosm/microcosm ↔ Adam Kadmon ↔ Purusha"],
  }),
  T({
    slug: "kabbalah", name: "Kabbalah",
    era: "12th century CE — present (roots earlier)",
    region: "Sephardic / Ashkenazi Jewry → global",
    summary: "Jewish mysticism — a map of the divine emanations (Sephirot) by which Ein Sof (the boundless) becomes the world, and a path for the soul to ascend back through them.",
    beliefs: [
      "Ein Sof: the absolute, beyond all attributes.",
      "The Tree of Life: ten Sephirot through which divinity flows.",
      "Tikkun olam: the soul's task is to repair the world.",
      "Adam Kadmon: the primordial human as cosmic blueprint.",
    ],
    practices: ["Meditative contemplation of the Sephirot", "Hebrew letter mysticism", "Gematria (numerical interpretation)", "Visualization on the Tree of Life"],
    figures: ["Isaac Luria", "Moses de León", "Abraham Abulafia", "Gershom Scholem"],
    correlations: ["Ein Sof ↔ Brahman ↔ apophatic God", "Sephirot ↔ chakras ↔ emanative ladders"],
  }),
  T({
    slug: "alchemy", name: "Alchemy",
    era: "1st century CE — 18th century (esoteric continuation since)",
    region: "Hellenistic Egypt → Islamic world → Europe → China (parallel)",
    summary: "The art of transmutation — outwardly the proto-chemistry of turning base metal to gold, inwardly the symbolic transformation of the soul from lead-like density to gold-like wholeness.",
    beliefs: [
      "Prima materia: the unformed ground beneath every form.",
      "Solve et coagula: dissolve, then reconstitute.",
      "The four elements + a fifth (quintessence / spirit).",
      "The Philosopher's Stone as the symbol of completed selfhood.",
    ],
    practices: ["The opus (laboratory work)", "Inner alchemy (meditative parallel to lab stages)", "Dream and symbol analysis", "Cultivation through nigredo → albedo → rubedo"],
    figures: ["Maria the Jewess", "Geber (Jabir ibn Hayyan)", "Paracelsus", "Carl Jung (re-interpreter)"],
    correlations: ["Philosopher's Stone ↔ Self (Jung) ↔ rigpa ↔ moksha", "Nigredo ↔ dark night of the soul"],
  }),
  T({
    slug: "gnosticism", name: "Gnosticism",
    era: "1st–4th century CE → modern revival",
    region: "Eastern Mediterranean → global esoteric streams",
    summary: "A constellation of early-Christian and pre-Christian sects teaching that liberation comes from gnosis — direct, experiential knowledge that the material world is a flawed copy of a higher pleroma, and the spark of the divine lies within.",
    beliefs: [
      "A hidden, ineffable God beyond the creator.",
      "The Demiurge: a lesser, often ignorant fabricator of the material world.",
      "Sophia: divine wisdom whose fall is the cosmic drama.",
      "Salvation through knowledge of one's true origin.",
    ],
    practices: ["Sacred reading (Nag Hammadi texts, Pistis Sophia)", "Initiatory rites", "Symbolic interpretation of scripture", "Awakening from the 'sleep' of forgetfulness"],
    figures: ["Valentinus", "Basilides", "Mary Magdalene (in Gnostic gospels)"],
    correlations: ["Gnosis ↔ jnana ↔ direct knowing", "Sophia ↔ Shakti ↔ Holy Spirit (feminine)"],
  }),
  T({
    slug: "jungian-psychology", name: "Jungian Psychology",
    era: "Early 20th century — present",
    region: "Switzerland → global",
    summary: "Carl Jung's depth psychology — a translation of the mystical traditions into clinical language: archetypes, the collective unconscious, individuation, and the wedding of conscious ego to the larger Self.",
    beliefs: [
      "The collective unconscious: an inherited stratum of symbolic patterns.",
      "Archetypes: the Self, Shadow, Anima/Animus, Persona, Wise Old Man, etc.",
      "Individuation: the lifelong work of integrating the unconscious.",
      "Synchronicity: meaningful, acausal connection of inner and outer events.",
    ],
    practices: ["Dream analysis", "Active imagination", "Symbol amplification", "Reading mythology as a mirror of the psyche"],
    figures: ["Carl Jung", "Marie-Louise von Franz", "Erich Neumann", "James Hillman"],
    correlations: ["Self ↔ Atman ↔ Christ within ↔ Philosopher's Stone", "Shadow ↔ klesha ↔ the fall"],
  }),
  T({
    slug: "modern-physics", name: "Modern Physics",
    era: "20th century — present",
    region: "Global scientific community",
    summary: "Relativity, quantum mechanics, and cosmology — the empirical edge where the classical billiard-ball universe dissolves into observer-dependent fields, non-locality, and a single underlying substrate.",
    beliefs: [
      "Mass-energy equivalence (E=mc²).",
      "Wave-particle duality and the observer's role in measurement.",
      "Quantum entanglement: non-local correlation between distant systems.",
      "Spacetime curvature as gravity; possibly emergent from deeper information.",
    ],
    practices: ["Experiment, replication, falsification", "Mathematical modelling", "Peer review", "Computational simulation"],
    figures: ["Albert Einstein", "Niels Bohr", "Werner Heisenberg", "David Bohm", "Carlo Rovelli"],
    correlations: ["Non-locality ↔ Indra's Net ↔ interbeing", "Observer effect ↔ consciousness traditions"],
  }),
  T({
    slug: "indigenous-australian", name: "Indigenous Australian",
    era: "65,000+ years ago — present",
    region: "Australian continent",
    summary: "The oldest continuous spiritual tradition on Earth — a relational cosmology in which the Dreaming (the Tjukurpa / Altjira) is the eternal creative time, ever-present beneath the ordinary, encoded in land, story, and song.",
    beliefs: [
      "The Dreaming: ancestral beings shaped the land and continue to inhabit it.",
      "Country is conscious and kin.",
      "Songlines: narrative maps that are also sacred trajectories.",
      "Reciprocity and responsibility to land and ancestors.",
    ],
    practices: ["Ceremony (corroboree)", "Songlines and oral transmission", "Dot painting and sand drawings as cosmological maps", "Initiation rites tied to landscape"],
    figures: ["Tradition is custodial — knowledge held by elders across hundreds of language groups"],
    correlations: ["Dreaming ↔ akasha ↔ aevum", "Songlines ↔ ley lines ↔ sacred geography"],
  }),
  T({
    slug: "mesoamerican", name: "Mesoamerican",
    era: "~2000 BCE — present (Maya / Aztec / Nahua / Zapotec)",
    region: "Mexico, Guatemala, Honduras, Belize",
    summary: "A family of cosmologies — Maya, Mexica/Aztec, Toltec — featuring precise calendars, a layered cosmos, and the conviction that human ceremonial action sustains the world.",
    beliefs: [
      "A layered cosmos: thirteen heavens, nine underworlds, this middle earth.",
      "Cycles: the Tzolk'in (260 days), Haab' (365 days), Long Count.",
      "Quetzalcoatl / Kukulkan: the feathered serpent uniting heaven and earth.",
      "Reciprocity with deities through ritual offering.",
    ],
    practices: ["Calendar divination (day-keeping)", "Pyramid ritual at solstices/equinoxes", "Sacred plant ceremonies (cacao, sacred mushroom)", "Mythic re-enactment"],
    figures: ["Maya day-keepers (ajq'ij)", "Don Juan Matus (Castañeda, contested)", "Modern teachers like Don Miguel Ruiz"],
    correlations: ["Quetzalcoatl ↔ kundalini ↔ Christ", "Calendar cycles ↔ yugas ↔ aeons"],
  }),
  T({
    slug: "siberian-shamanism", name: "Siberian Shamanism",
    era: "~30,000 years ago — present",
    region: "Siberia, Mongolia, Arctic Circle (paradigm for global shamanism)",
    summary: "The archetypal shamanic tradition — practitioners enter altered states to travel the three worlds (upper, middle, lower) on behalf of their community, retrieving lost souls and brokering with spirits.",
    beliefs: [
      "Three worlds connected by the world tree / axis mundi.",
      "All things have spirit (animism).",
      "Soul loss as a cause of illness; soul retrieval as healing.",
      "Ancestors and spirit guides as collaborators.",
    ],
    practices: ["Drumming and rhythmic trance", "Spirit journeying", "Vision quest", "Healing rituals, soul retrieval, psychopomp work"],
    figures: ["Tungus and Buryat lineages", "Modern interpreters: Mircea Eliade, Michael Harner"],
    correlations: ["World tree ↔ Tree of Life (Kabbalah) ↔ axis mundi", "Trance states ↔ samadhi ↔ contemplative absorption"],
  }),
  T({
    slug: "native-american-shamanism", name: "Native American Shamanism",
    era: "~15,000+ years ago — present",
    region: "Turtle Island (North America) — hundreds of distinct Nations",
    summary: "A vast family of Indigenous North American cosmologies — each Nation with its own languages, ceremonies, and clan structures — sharing a felt kinship with the land, the four directions, and the visible and unseen relatives that hold the web of life together.",
    beliefs: [
      "All my relations — kinship extends to plants, animals, stones, wind, and ancestors.",
      "The four directions (and often seventh: above, below, within) as sacred orientation.",
      "The Great Spirit / Great Mystery — pervasive, present, unnameable.",
      "Dreams and visions are legitimate ways of knowing and receiving instruction.",
    ],
    practices: [
      "Vision quest (hanbleceya) and rites of passage",
      "Sweat lodge (inipi) for purification and prayer",
      "Sacred pipe (chanunpa) ceremony",
      "Sun Dance, medicine wheel work, plant medicine (tobacco, sage, cedar, sweetgrass)",
      "Storytelling and song as living transmission",
    ],
    figures: [
      "Tradition is custodial — knowledge held by elders, Chiefs, and medicine people across many Nations",
      "Public teachers who wrote openly: Black Elk (Oglala Lakota), Fools Crow, Vine Deloria Jr.",
    ],
    correlations: [
      "Great Mystery ↔ Brahman ↔ Ain Soph ↔ Tao",
      "Medicine wheel ↔ mandala ↔ zodiac ↔ Kabbalistic tree",
      "Vision quest ↔ desert fathers ↔ Bodhi tree ↔ Muhammad's cave retreat",
    ],
  }),
  T({
    slug: "western-astrology", name: "Western Astrology",
    era: "~2000 BCE — present (Babylonian → Hellenistic → modern)",
    region: "Mesopotamia → Mediterranean → Europe → global",
    summary: "A symbolic language reading the heavens as a mirror of earthly experience — twelve signs of the zodiac, ten planets, twelve houses, and the natal chart as a portrait of the soul's particular angle of incarnation.",
    beliefs: [
      "'As above, so below' — celestial bodies correspond to inner faculties.",
      "The natal chart maps temperament, vocation, and karmic themes.",
      "Transits and progressions reflect the timing of inner unfolding.",
      "Free will operates within — not against — astrological pattern.",
    ],
    practices: ["Natal chart interpretation", "Transit and progression work", "Electional and horary astrology", "Synastry (relationship charts)"],
    figures: ["Ptolemy", "Marsilio Ficino", "Dane Rudhyar", "Liz Greene", "Steven Forrest"],
    correlations: ["Zodiacal archetypes ↔ Jungian archetypes ↔ Tarot Major Arcana", "'As above, so below' ↔ Hermetic correspondence"],
  }),
  T({
    slug: "vedic-astrology", name: "Vedic Astrology",
    era: "~1500 BCE — present",
    region: "Indian subcontinent → global",
    summary: "Jyotish — 'the science of light' — the Hindu astrological system rooted in the sidereal zodiac and the 27 lunar nakshatras, viewed as a karmic diagnostic and a roadmap for dharma.",
    beliefs: [
      "Karma made visible: the chart reveals the soul's prior trajectories.",
      "Sidereal zodiac (fixed against the stars) rather than tropical.",
      "27 nakshatras: lunar mansions of fine-grained meaning.",
      "Dasha system: planetary periods that time the unfolding of karmas.",
    ],
    practices: ["Natal Jyotish chart reading", "Dasha and gochar (transit) analysis", "Remedial measures (mantras, gemstones, charity)", "Muhurta (electing auspicious times)"],
    figures: ["Parashara (legendary)", "Varahamihira", "Bepin Behari", "Komilla Sutton"],
    correlations: ["Karma in the chart ↔ samskaras ↔ unconscious patterning", "Nakshatras ↔ archetypal images"],
  }),
  T({
    slug: "human-design", name: "Human Design",
    era: "1987 — present",
    region: "Synthetic system (Ra Uru Hu, Ibiza) → global",
    summary: "A modern synthesis — astrology + I Ching + Kabbalah + chakra system + quantum biology — proposing that each person has a unique inner mechanic ('type', 'authority', 'strategy') for navigating reality without resistance.",
    beliefs: [
      "Four types: Manifestor, Generator, Projector, Reflector.",
      "Each has an inner authority — the body's truer decision-maker than the mind.",
      "Defined and undefined energy centers shape consistent vs. conditioned traits.",
      "Following your strategy creates flow; mind-led living creates resistance.",
    ],
    practices: ["Reading your bodygraph", "Experimenting with your strategy + authority", "De-conditioning open centers", "Aligning daily decisions with type"],
    figures: ["Ra Uru Hu (Alan Krakower)", "Karen Curry Parker", "Chetan Parkyn"],
    correlations: ["Chakra centers ↔ Sephirot ↔ Hermetic principles", "Inner authority ↔ pratibha (inner light) ↔ conscience"],
  }),
  T({
    slug: "quantum-physics", name: "Quantum Physics",
    era: "1900 — present",
    region: "Global scientific community",
    summary: "The empirical science of the very small — where the classical, billiard-ball picture of reality dissolves into probability waves, observer-dependent measurement, and non-local connection. For many it's also a doorway: the place where rigorous science begins to rhyme with what the traditions have long whispered.",
    beliefs: [
      "Reality at small scales is probabilistic, not deterministic.",
      "Wave-particle duality: matter behaves as both wave and particle depending on how it's looked at.",
      "The act of measurement participates in what is measured (observer effect).",
      "Entanglement: once-correlated systems remain instantaneously linked across any distance.",
      "Superposition: a system holds many possibilities until interaction collapses them into one.",
    ],
    practices: ["Experiment + falsification", "Mathematical formalism (Hilbert space, operators)", "Thought experiments (Schrödinger's cat, EPR, double-slit)", "Quantum computation + information theory", "Interpretive philosophy (Copenhagen, Many-Worlds, Bohmian, QBism)"],
    figures: ["Max Planck", "Niels Bohr", "Werner Heisenberg", "Erwin Schrödinger", "David Bohm", "John Bell", "Anton Zeilinger"],
    correlations: ["Observer effect ↔ consciousness as participant (Vedanta, Idealism)", "Entanglement ↔ Indra's Net ↔ interbeing", "Superposition ↔ pre-manifest potential (Tao, Ein Sof)"],
  }),
  T({
    slug: "law-of-one", name: "The Law of One",
    era: "1981–1984 (channeled); ongoing study",
    region: "Kentucky, USA → global esoteric communities",
    summary: "A modern channeled framework — the 'Ra Material' transmitted through L/L Research — that names the foundational principle plainly: there is no other. All apparent separation (self/other, light/dark, matter/spirit) is the One Infinite Creator catching glimpses of itself through density, free will, and time. A cosmology built around two paths of polarization: service-to-others and service-to-self.",
    beliefs: [
      "All is One: every apparent other is the Creator experiencing itself.",
      "Free will is the first distortion; without it, the One could not seek itself.",
      "Existence proceeds through 'densities' (octaves of consciousness) — from mineral, to plant, to human, to social-memory complex, and beyond.",
      "Polarity: each being chooses, in this density, between service-to-others and service-to-self. Both polarize equally; the unrefined middle does not.",
      "The Logos shapes each density's lessons; ours are the lessons of love (third density) and wisdom (fourth).",
    ],
    practices: ["Meditation (silent, listening)", "Daily 'balancing' of opposing inner qualities", "Contemplation of catalyst — treating life events as teaching", "Service: choosing to act for the One in each encounter", "Study circles around the 'Ra Material' (Books I–V)"],
    figures: ["Don Elkins", "Carla L. Rueckert", "Jim McCarty", "Ra (the channeled source)"],
    correlations: ["All is One ↔ Advaita's Brahman ↔ Sufism's Tawhid ↔ Hermetic 'The All'", "Density / octave ↔ Jacob's Ladder ↔ chakra ascent ↔ alchemical stages", "Service-to-others ↔ bodhisattva vow ↔ agape ↔ tikkun olam", "Free will ↔ Christian liberum arbitrium ↔ karmic responsibility"],
  }),
  T({
    slug: "secular-skeptic", name: "Secular / Skeptic",
    era: "Antiquity — present (modern form: 17th-c. Enlightenment onward)",
    region: "Global",
    summary: "A non-religious stance that grounds claims in evidence and reason, holds beliefs proportional to that evidence, and is willing to suspend conclusion where the data is insufficient.",
    beliefs: [
      "Extraordinary claims require extraordinary evidence.",
      "Methodological naturalism: explanations sought within nature.",
      "Falsifiability as a hallmark of meaningful claims.",
      "Ethics grounded in human flourishing, not metaphysical decree.",
    ],
    practices: ["Critical thinking", "Bayesian updating", "Steel-manning opposing views", "Tolerance of uncertainty"],
    figures: ["David Hume", "Bertrand Russell", "Carl Sagan", "Richard Feynman", "Daniel Dennett"],
    correlations: ["Apatheia (Stoic) ↔ equanimity ↔ scientific detachment", "Falsifiability ↔ neti neti ('not this, not that')"],
  }),
  T({
    slug: "spiritual-but-not-religious", name: "Spiritual but not religious",
    era: "20th century — present (broad contemporary current)",
    region: "Global, urban / post-traditional",
    summary: "A late-modern orientation that values direct experience over inherited dogma — drawing freely from many traditions, anchored in personal practice and ethical living rather than institutional membership.",
    beliefs: [
      "Reality has depth that science alone cannot exhaust.",
      "Authority lies in lived experience, not in scripture or hierarchy.",
      "Traditions are tools; the practitioner is the laboratory.",
      "Love and presence are the practical heart.",
    ],
    practices: ["Eclectic meditation", "Reading across traditions", "Plant medicine ceremonies (some)", "Nature, breathwork, embodiment practices"],
    figures: ["Alan Watts", "Ram Dass", "Eckhart Tolle", "Pema Chödrön", "Adyashanti"],
    correlations: ["Direct experience ↔ gnosis ↔ pratyabhijna", "Trans-traditional drawing ↔ perennial philosophy"],
  }),
];

export const TRADITION_BY_SLUG = Object.fromEntries(TRADITIONS.map((t) => [t.slug, t]));

// Used by the Settings "Traditions that feel like home" chooser — just names.
export const TRADITION_NAMES = TRADITIONS.map((t) => t.name);
